import { Buffer } from 'buffer';
import * as Sentry from "@sentry/nuxt";

export const useBookingStore = defineStore('booking', {
  state: () => {
    return {
      moveReservation: null,
      rebookReservation: null,
      waitlist: null,
      serviceSelections: {},
      selectedSlot: null,
      selectedCustomer: null,
      selectedDate: null,
      mode: 'standard', // options standard, timetable, lastminute
      submission: null,
      voucher: null,
      pass: null,
      giftCard: null,
      guid: 'booking_' + (new Date()).getTime(),
      payment: {},
      filters: {}
    }
  },
  actions: {
    clear() {
      this.moveReservation = null
      this.rebookReservation = null
      this.waitlist = null
      this.serviceSelections = {}
      this.selectedSlot = null
      this.selectedCustomer = null
      this.selectedDate = null
      this.mode = 'standard'
      this.submission = null
      this.voucher = null
      this.pass = null
      this.giftCard = null
      this.guid = 'booking_' + (new Date()).getTime()
      this.payment = {}
      this.filters = {}
    },
    setServiceSelection({ service, selected, employee, order, disabled }) {
      let serviceSelection = this.serviceSelections[service.id]

      if (!serviceSelection) {
        serviceSelection = {}
        serviceSelection.service = service
        serviceSelection.selectedEmployee = null
        serviceSelection.selected = false
        serviceSelection.disabled = false
        serviceSelection.order = 0
      }

      if (selected !== undefined) serviceSelection.selected = selected
      if (employee !== undefined) serviceSelection.selectedEmployee = employee
      if (order !== undefined) serviceSelection.order = order
      if (disabled !== undefined) serviceSelection.disabled = disabled

      // Vue.set(this.serviceSelections, service.id, serviceSelection)

      this.serviceSelections[service.id] = serviceSelection
    },
    clearServiceSelections() {
      this.serviceSelections = {}
    },
    setSelectedDate(date) {
      this.selectedDate = date
    },
    setSelectedSlot({ slot, mode }) {
      this.selectedSlot = slot
      this.mode = mode
    },
    setSelectedCustomer(customer) {
      this.selectedCustomer = customer
    },
    setMode(mode) {
      this.mode = mode
    },
    setSubmission(submission) {
      this.submission = submission
    },
    setVoucher(voucher) {
      this.voucher = voucher
    },
    setPass(pass) {
      this.pass = pass
    },
    setGiftCard(giftCard) {
      this.giftCard = giftCard
    },
    setPayment({ guid, payment }) {
      if (payment) {
        this.payment[guid] = payment
      } else {
        delete this.payment[guid]
      }
    },
    setFilter({ filter, value }) {
      if (filter) {
        this.filters[filter] = value
      } else {
        delete this.filters[filter]
      }
    },
    setFilters(filters) {
      this.filters = filters
    },
    setMoveReservation({ moveReservation, includeEmployees = false }) {
      this.moveReservation = moveReservation

      const year = moveReservation.date.toString().substring(0, 4)
      const month = moveReservation.date.toString().substring(4, 6)
      const day = moveReservation.date.toString().substring(6, 8)

      this.setSelectedDate(new Date(year, month - 1, day))

      for (let i = 0; i < moveReservation.services.length; i++) {
        const reservationService = moveReservation.services[i]
        const employee = (includeEmployees) ? reservationService.employee : null

        this.setServiceSelected({ service: reservationService.service, employee, selected: true, toBack: true })
      }

      if (moveReservation.voucher) {
        this.setVoucher(moveReservation.voucher)
      }

      if (moveReservation.pass) {
        this.setPass(moveReservation.pass)
      }
    },
    setRebookReservation(rebookReservation) {
      this.rebookReservation = rebookReservation

      this.setSelectedDate(new Date())

      for (let i = 0; i < rebookReservation.services.length; i++) {
        const reservationService = rebookReservation.services[i]

        this.setServiceSelected({ service: reservationService.service, selected: true, toBack: true })
      }
    },
    setWaitlist({ waitlist, moveReservation = null } = {}) {
      this.waitlist = waitlist

      if (moveReservation) {
        this.moveReservation = moveReservation
      }
    },
    setServiceSelected({ service, selected, employee, toBack, reorderAndDisable }) {
      if (reorderAndDisable === undefined) reorderAndDisable = true
      if (toBack === undefined) {
        const selection = this.serviceSelections[service.id]
        if (!selection || !selection.selected) {
          toBack = true
        }
      }

      if (reorderAndDisable === undefined) reorderAndDisable = true
    if (toBack === undefined) {
      const selection = this.serviceSelections[service.id]
      if (!selection || !selection.selected) {
        toBack = true
      }
    }

      const locationStore = useLocationStore()
      const services = locationStore.services
      // get full version of service if available
      if (services) {
        for (let i = 0; i < services.categories.length; i++) {
          const category = services.categories[i]
          for (let j = 0; j < category.services.length; j++) {
            if (category.services[j].id == service.id) {
              service = category.services[j]
            }
          }
        }
      }

      this.setServiceSelection({ service, selected, employee })

      if (toBack) {
        this.setServiceSelection({ service, order: (this.maxOrder + 1) })
      }

      if (services) {
        for (let i = 0; i < services.categories.length; i++) {
          const category = services.categories[i]
          for (let j = 0; j < category.services.length; j++) {
            if (category.services[j].id !== service.id) {
              this.setServiceSelection({ service: category.services[j], disabled: false })
            }
          }
        }
      }

      if (reorderAndDisable) {
        this.reorder()
        this.disableServices()
      }
    },
    reorder() {
      const serviceOrders = []

      for (const id in this.serviceSelections) {
        if (this.serviceSelections[id]) {
          const selection = this.serviceSelections[id]

          if (selection.selected) {
            const serviceInfo = {}
            serviceInfo.service = selection.service
            serviceInfo.order = selection.order

            serviceOrders.push(serviceInfo)
          }
        }
      }

      serviceOrders.sort(function (a, b) {
        return (a.order - b.order)
      })

      for (let i = 0; i < serviceOrders.length; i++) {
        this.setServiceSelection({ service: serviceOrders[i].service, order: (i + 1) })
      }
    },
    disableServices() {
      let hasGroupSelected = false
      let hasNonGroupSelected = false

      const locationStore = useLocationStore()
      const services = locationStore.getAllServices

      for (let i = 0; i < services.length; ++i) {
        const selection = this.serviceSelections[services[i].id]
        if (selection && selection.selected) {
          if (services[i].customerType === 1) {
            hasGroupSelected = true
          } else {
            hasNonGroupSelected = true
          }
        }
      }

      for (let i = 0; i < services.length; ++i) {
        const selection = this.serviceSelections[services[i].id]
        if (!selection || !selection.selected) {
          if (hasGroupSelected) {
            this.setServiceSelection({ service: services[i], disabled: true })
          } else if (hasNonGroupSelected && services[i].customerType === 1) {
            this.setServiceSelection({ service: services[i], disabled: true })
          } else {
            this.setServiceSelection({ service: services[i], disabled: false })
          }
        }
      }
    },
    async setFromUrlQuery(query = {}) {
      this.clear()

      const locationStore = useLocationStore()
      const { $api } = useNuxtApp()

      const currentQuery = this.urlQuery

      let servicesPromise = null
      let slotPromise = null
      let rebookPromise = null
      let movePromise = null
      let voucherPromise = null
      let passPromise = null

      if (((query.services && currentQuery.services !== query.services) || (query.slot && currentQuery.slot !== query.slot)) && !locationStore.services) {
        servicesPromise = locationStore.getServices()
      }

      if (!query.slot) {
        if (slotPromise) {
          this.setSelectedSlot({})
        }
      } else if (query.slot && currentQuery.slot !== query.slot) {
        slotPromise = $api.booking.slot(query.slot)
      }

      if (query.rebook && currentQuery.rebook !== query.rebook) {
        const rebookJson = Buffer.from(query.rebook, 'base64').toString()
        const rebookObject = JSON.parse(rebookJson)

        rebookPromise = $api.customer.reservation(rebookObject.id, rebookObject.date, { id: rebookObject.customer }, rebookObject.signature)
      }
      if (query.move && currentQuery.move !== query.move) {
        const moveJson = Buffer.from(query.move, 'base64').toString()
        const moveObject = JSON.parse(moveJson)

        movePromise = $api.customer.reservation(moveObject.id, moveObject.date, { id: moveObject.customer }, moveObject.signature)
      }

      if (query.voucher && currentQuery.voucher !== query.voucher) {
        voucherPromise = $api.booking.voucher(query.voucher).catch((error) => error)
      }

      if (query.pass && currentQuery.pass !== query.pass) {
        const passJson = Buffer.from(query.pass, 'base64').toString()
        const passObject = JSON.parse(passJson)

        passPromise = $api.booking.customerPass(passObject.id, passObject.signature)
      }

      await Promise.all([servicesPromise, slotPromise, rebookPromise, movePromise, voucherPromise, passPromise]).then((values) => {
        if (slotPromise) this.setSelectedSlot({ slot: values[1].slot })
        if (rebookPromise) this.rebookReservation = values[2].reservation
        if (movePromise) this.moveReservation = values[3].reservation
        if (voucherPromise) this.setVoucher(values[4]?.voucher)
        if (passPromise) this.setPass(values[5].customerPass)

        if (values[1] && values[1].slot) {
          for (let i = 0; i < values[1].slot.services.length; i++) {
            const slotService = values[1].slot.services[i]

            this.setServiceSelection({ service: slotService.service, employee: slotService.employee, selected: true, order: (i + 1) })
          }

          this.disableServices()
        }
      }).catch((e: any) => {
        // console.log(e)
      })

      if (!query.services && !query.slot) {
        this.clearServiceSelections()
      } else if (query.services && currentQuery.services !== query.services) {
        const json = Buffer.from(query.services, 'base64').toString()
        const serviceData = JSON.parse(json)

        const services = locationStore.getAllServices

        for (let i = 0; i < serviceData.length; i++) {
          const serviceInfo = serviceData[i]
          const service = services.find((service) => {
            return (service.id == serviceInfo.s)
          })

          if (service) {
            let employeeService = null
            if (serviceInfo.e) {
              employeeService = service.employeeServices.find((employeeService) => {
                return (employeeService.employee.id == serviceInfo.e)
              })
            }

            let employee = null

            if (employeeService && employeeService.employee) {
              employee = employeeService.employee
            }

            this.setServiceSelection({ service, employee, selected: true, order: (i + 1) })
          }
        }

        this.disableServices()
      }

      // this.setMode((query.mode) ? query.mode : 'standard')
      // this.setWaitlist((query.waitlist) ? query.waitlist : null)
      // this.setGiftCard((query.giftcard) ? query.giftcard : null)
      this.mode = (query.mode) ? query.mode : 'standard'
      this.waitlist = (query.waitlist) ? query.waitlist : null
      this.giftCard = (query.giftcard) ? query.giftcard : null

      if (!query.customer) {
        this.setSelectedCustomer(null)
      } else if (query.customer && currentQuery.customer !== query.customer) {
        const json = Buffer.from(query.customer, 'base64').toString()
        const customer = JSON.parse(json)

        this.setSelectedCustomer(customer)
      }

      if (!query.date) {
        this.setSelectedDate(null)
      } else if (query.date && currentQuery.date !== query.date) {
        this.setSelectedDate(new Date(parseInt(query.date)))
      }

      this.setFilters({})

      for (const [key, value] of Object.entries(query)) {
        if (key.startsWith('f.')) {
          const filter = key.substring(2)
          this.setFilter({ filter, value })
        }
      }
    }
  },
  getters: {
    selectedServices(state) {
      const selectedServices = []

      const serviceSelections = state.serviceSelections

      for (const id in serviceSelections) {
        if (serviceSelections[id]) {
          const selection = serviceSelections[id]

          if (selection.selected) {
            selectedServices.push(selection)
          }
        }
      }
      selectedServices.sort(function (a, b) {
        return (a.order - b.order)
      })

      return selectedServices
    },
    maxOrder(state) {
      let maxOrder = 0

      const serviceSelections = state.serviceSelections

      for (const id in serviceSelections) {
        if (serviceSelections[id]) {
          const selection = serviceSelections[id]

          if (selection.selected) {
            if (selection.order > maxOrder) {
              maxOrder = selection.order
            }
          }
        }
      }

      return maxOrder
    },
    urlQuery(state) {
      const query = {}
      if (state.serviceSelections) {
        const selectedServices = []
        const serviceSelections = state.serviceSelections

        for (const id in serviceSelections) {
          if (serviceSelections[id]) {
            const selection = serviceSelections[id]

            if (selection.selected) {
              selectedServices.push(selection)
            }
          }
        }

        selectedServices.sort(function (a, b) {
          return (a.order - b.order)
        })

        const serviceData = selectedServices.map((serviceSelection) => {
          const serviceInfo = { s: serviceSelection.service.id }
          if (serviceSelection.selectedEmployee) serviceInfo.e = serviceSelection.selectedEmployee.id
          return serviceInfo
        })

        if (serviceData.length > 0) {
          query.services = Buffer.from(JSON.stringify(serviceData)).toString('base64')
        }
      }
      if (state.selectedSlot) {
        query.slot = state.selectedSlot.data
      }
      if (state.selectedDate) {
        query.date = state.selectedDate.getTime()
      }
      if (state.selectedCustomer) {
        query.customer = Buffer.from(JSON.stringify(state.selectedCustomer)).toString('base64')
      }
      if (state.submission) {
        query.submission = Buffer.from(JSON.stringify(state.submission)).toString('base64')
      }
      if (state.rebookReservation) {
        const rebookObject = {
          id: state.rebookReservation.id,
          date: state.rebookReservation.date,
          customer: state.rebookReservation.customer.id,
          signature: state.rebookReservation.signature
        }
        query.rebook = Buffer.from(JSON.stringify(rebookObject)).toString('base64')
      }
      if (state.moveReservation) {
        const moveObject = {
          id: state.moveReservation.id,
          date: state.moveReservation.date,
          customer: state.moveReservation.customer.id,
          signature: state.moveReservation.signature
        }
        query.move = Buffer.from(JSON.stringify(moveObject)).toString('base64')
      }

      if (state.voucher) {
        query.voucher = state.voucher.code
      }

      if (state.pass) {
        const passObject = {
          id: state.pass.id,
          signature: state.pass.signature
        }
        query.pass = Buffer.from(JSON.stringify(passObject)).toString('base64')
      }

      if (state.giftCard) {
        query.giftcard = state.giftCard
      }

      if (state.mode !== 'standard') {
        query.mode = state.mode
      }

      if (state.waitlist) {
        query.waitlist = state.waitlist
      }

      if (state.filters) {
        for (const [key, value] of Object.entries(state.filters)) {
          query['f.' + key] = value
        }
      }

      return query
    }
  }
})
